import { useEffect, useMemo, useRef, useState } from 'react';
import {
  ComposedChart,
  Bar,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  ResponsiveContainer,
  Cell,
} from 'recharts';
import { useTranslation } from 'react-i18next';
import { formatDate } from '../helpers';
import styles from './battery-chart.module.scss';
import { useSelector } from 'react-redux';
import { BatterySkeleton, Shimmer } from '@flexo/atoms';
import { useLocation } from 'react-router-dom';
import _ from 'lodash';
import { getNumberFormatLocale } from '@flexo/general';
import { maskKpiDataAfterUpperBound } from '../helpers';

const ENV = (import.meta as any).env;


export function BatteryChart(props: any) {
  const { t } = useTranslation();
  const location = useLocation();
  const pathSegment = location.pathname.split('/')[1];
  const pathContext = pathSegment.split('-')[0];
  const kpi = useSelector((state: any) => state.kpi);
  const calendar = useSelector((state: any) => state.calendar);
  const calendarUpperBound = useSelector(
    (state: any) => state?.appConfig?.calendar?.upperBound || null
  );
  const { selectedTimeSet } = calendar.hiveCalendar;
  const ThemeStore = useSelector((state: any) => state.theme);

  const [unit, setUnit] = useState('');
  const [chartData, setChartData] = useState<any[]>([]);
  const [activeIndex, setActiveIndex] = useState<string | number | null>(null);
  const [activeDataSets, setActiveDataSets] = useState<any[]>([]);
  const [isNullStateActive, setNullStateActive] = useState<boolean>(false);
  const [innerTimeSet, setInnerTimeSet] = useState<string>(selectedTimeSet);
  const chartRef = useRef<any>(null);
  const xRef = useRef<any>(null);

  // Determine if we are in a web environment (desktop)
  const isWeb = ENV?.VITE_APP_IS_WEB === 'true';

  // Determine entity and kpiContext from the path
  let entity = 'Site';
  let kpiContext = 'Energy';
  if (pathSegment?.includes('economics')) kpiContext = 'Economics';
  if (pathSegment?.includes('community')) entity = 'Community';
  if (pathSegment?.includes('member')) entity = 'Member';

  const rawData = kpi?.[`${kpiContext}${entity}`]?.['1hour'] || null;
  const displayData = useMemo(
    () => maskKpiDataAfterUpperBound(rawData, calendarUpperBound),
    [rawData, calendarUpperBound]
  );
  const LOADING_STATE = kpi?.[`${kpiContext}KpiData${entity}AsyncStatus`];

  function getGreyScaledColor(color: string) {
    let _color = color;
    if (color.includes('#')) {
      const red = parseInt(color.substr(1, 2), 16);
      const green = parseInt(color.substr(3, 2), 16);
      const blue = parseInt(color.substr(5, 2), 16);
      // Convert color to grayscale
      const grayscale = Math.round(0.299 * red + 0.587 * green + 0.114 * blue);
      const grayscaleHex = grayscale.toString(16).padStart(2, '0');
      _color = '#' + grayscaleHex + grayscaleHex + grayscaleHex;
    }
    return _color;
  }

  const CustomizedDot = (_props: any) => {
    const { cx, cy, payload, index } = _props;
    // Different colors and shades for web and mobile
    const colorIndex = isWeb ? 1 : 2;
    const colorShadeIndex = isWeb ? 1 : 1;
    return (
      <circle
        key={`dot-${payload.timestamp}`}
        cx={isWeb ? cx : cx - 2}
        cy={cy}
        r={activeIndex !== null && parseInt(activeIndex as string) === index ? 8 : 5}
        fill={
          activeIndex !== null && parseInt(activeIndex as string) === index
            ? `${ThemeStore?.colors[props.colors[colorIndex].color]}${props.colors[colorShadeIndex].colorShade}`
            : 'transparent'
        }
        stroke="none"
        onMouseEnter={isWeb ? () => setActiveIndex(`${index}`) : undefined}
        onMouseLeave={isWeb ? () => setActiveIndex(null) : undefined}
        onTouchEnd={!isWeb ? handleTouchEnd : undefined}
        onTouchEndCapture={!isWeb ? handleTouchEnd : undefined}
        style={{ transition: 'r 0.2s ease' }}
      />
    );
  };

  // Event handlers
  const handleMouseMove = (index: number) => isWeb && setActiveIndex(`${index}`);
  const handleMouseLeave = () => isWeb && setActiveIndex(null);
  const handleBarClick = (index: number) => isWeb && setActiveIndex(`${index}`);

  // Touch logic (mobile only)
  const handleTouchMove = (e: any) => {
    if (!chartRef.current || !e.touches || e.touches.length === 0) return;
    const xReference = document.getElementsByClassName('recharts-bar')[0];
    if (!xReference) return;
    const touchX = e.touches[0].clientX;
    let { left, right } = (xReference as any).getBoundingClientRect();
    left = 50;
    right = innerTimeSet === 'day' ? right : right - 60;
    const relativeX = touchX - left;
    const index = Math.round((relativeX / right) * (chartData.length - 1));
    const clampedIndex: any = Math.max(0, Math.min(index, chartData.length - 1));
    setActiveIndex(clampedIndex);
  };
  const handleTouchEnd = (e?: any) => {
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }
    setTimeout(() => setActiveIndex(null), 100);
  };

  // Format button value for display
  function formatButtonValue(fieldName: string | null, buttonString: string, value: number | null) {
    let formattedValue = `${buttonString}`;
    if (value === null && buttonString) {
      if (fieldName === 'soc_battery') return `${buttonString} (%)`;
      return formattedValue;
    }
    switch (fieldName) {
      case 'battery_net_flow':
        formattedValue = `${buttonString} | ${value} ${unit}`;
        break;
      case 'soc_battery':
        formattedValue = `${buttonString} | ${value} %`;
        break;
      default:
        formattedValue = `${buttonString} | ${value} ${unit}`;
        break;
    }
    return formattedValue;
  }

  // Remove duplicate or -0 values from array
  function cleanArray(arr: any) {
    const uniqueSet = new Set();
    const result: any = [];
    for (let num of arr) {
      if (Object.is(num, -0)) num = 0;
      if (!uniqueSet.has(num)) {
        uniqueSet.add(num);
        result.push(num);
      }
    }
    return result;
  }

  // Calculate symmetric ticks for Y axis
  function getSymmetricTicks(data: any): number[] {
    const values = isWeb
      ? data.map((d: any) => d?.[props.db_fields?.[0] || 0])
      : data.map((d: any) => [d?.[props.db_fields?.[0]] || 0, d?.[props.db_fields?.[1]] || 0]).flat();
    const maxValue = Math.max(...values);
    const minValue = Math.min(...values);
    const maxAbs = Math.max(Math.abs(maxValue), Math.abs(minValue));
    const roundedMaxAbs = Number(maxAbs.toFixed(2));
    return cleanArray([
      roundedMaxAbs,
      Number((roundedMaxAbs / 2).toFixed(2)),
      0,
      Number((-roundedMaxAbs / 2).toFixed(2)),
      -roundedMaxAbs,
    ]);
  }

  function getFillColor(index: any, colorIndex: number): string {
    if (activeIndex === null) {
      return `${ThemeStore?.colors[props.colors[colorIndex].color]}${props.colors[colorIndex].colorShade}`;
    } else if (parseInt(activeIndex as string) === parseInt(index)) {
      return `${ThemeStore?.colors[props.colors[colorIndex].color]}${props.colors[colorIndex].colorShade}`;
    } else {
      return `${ThemeStore?.colors[props.colors[colorIndex].color]}60`;
    }
  }

  // Toggle active datasets for chart
  function toggleDataSets(index: number) {
    if (activeDataSets?.length < (isWeb ? 2 : 3)) return;
    if (
      activeDataSets.length === (isWeb ? 2 : 3) &&
      activeDataSets.includes(false) &&
      activeDataSets.indexOf(true) === index
    ) {
      return;
    }
    const newActiveDataSets = [...activeDataSets];
    newActiveDataSets[index] = !newActiveDataSets[index];
    setActiveDataSets(newActiveDataSets);
  }

  // Get X axis interval based on time set and platform
  const getInterval = () => {
    if (isWeb) {
      switch (innerTimeSet) {
        case 'day':
        case 'week':
        case 'month':
          return 6;
        case 'year':
          return 2;
        default:
          return 0;
      }
    } else {
      switch (innerTimeSet) {
        case 'day':
        case 'month':
          return 6;
        case 'week':
          return 1;
        case 'year':
          return 2;
        default:
          return 0;
      }
    }
  };

  // useEffect for data and state
  useEffect(() => {
    // Create data for Recharts
    const _chartData = (displayData || []).map((item: any) => {
      const formattedData: any = {
        timestamp: formatDate(item.timestamp, innerTimeSet),
      };
      props?.db_fields.forEach((field: string) => {
        const kpiValue = item?.kpi.find((datum: any) => datum.name === field);
        if (kpiValue) {
          formattedData[field] = (!isWeb && field === 'self_consumption_battery')
            ? kpiValue.value * -1
            : kpiValue.value;
        }
      });
      return formattedData;
    });
    setChartData(_chartData);
    const nullCheck = _.cloneDeep(_chartData || [])
      .map((item) => {
        if (item['date']) delete item['date'];
        if (item['timestamp']) delete item['timestamp'];
        return Object(item);
      })
      .reduce((acc, item) => [
        ...acc,
        ...Object.entries(item).map((entry) => entry[1]),
      ], [])
      .every((item) => item === null);
    setNullStateActive(nullCheck);
    // Set unit of measurement
    if (props?.db_fields?.length > 0) {
      const firstField = props?.db_fields[0];
      const firstItem = displayData?.[0];
      if (firstItem) {
        const kpiValue = firstItem.kpi.find((datum: any) => datum.name === firstField);
        setUnit(kpiValue?.unit || '');
      }
      if (innerTimeSet === 'day' && props?.context === 'site') {
        setActiveDataSets(Array.from({ length: props?.db_fields?.length }, (_) => true));
      }
    }
  }, [displayData, props.db_fields, innerTimeSet, isWeb, props.context]);

  useEffect(() => {
    if (LOADING_STATE !== 'PENDING' && innerTimeSet !== selectedTimeSet) {
      setInnerTimeSet(selectedTimeSet);
    }
  }, [LOADING_STATE]);

  // Rendering
  return (
    <div
      id="CombinedBatteryChart__Wrapper"
      className={isWeb ? styles.CombinedBatteryChart__Wrapper__Web : styles.CombinedBatteryChart__Wrapper}
      style={isWeb ? undefined : { height: '70%', padding: '16px' }}
    >
      {LOADING_STATE === 'PENDING' && (
        <div style={isWeb ? {
          width: 'calc(100% - 30px)',
          height: 'calc(100% - 30px)',
          position: 'absolute',
          top: 20,
          left: 20
        } : undefined}><Shimmer /></div>
      )}
      <>
        <div
          className={
            activeIndex
              ? styles.CombinedBatteryChart__Title__Active
              : styles.CombinedBatteryChart__Title
          }
        >
          {activeIndex && rawData?.[activeIndex]?.timestamp && (
            <div className={styles.CombinedBatteryChart__TimeStamp}>
              {formatDate(rawData?.[activeIndex]?.timestamp, innerTimeSet)}
            </div>
          )}
          {t(props.title)}
        </div>
        <div className={isWeb
          ? styles.CombinedBatteryChart__Buttons__Wrapper
          : styles.CombinedBatteryChart__Buttons__Wrapper__Mobile}>
          {
            (LOADING_STATE === 'PENDING' && chartData.length === 0)
              ? <></>
              : props.buttons.map((button: any, index: number) => {
                // Condition to show buttons
                if (isWeb ? (index > 0 && innerTimeSet !== 'day') : (index > 1 && innerTimeSet !== 'day')) {
                  return '';
                }
                return (
                  <div
                    key={`BatteryChart__${index}__button__${button}}`}
                    className={styles.CombinedBatteryChart__Button}
                    onClick={() => toggleDataSets(index)}
                    style={{
                      opacity:
                        activeDataSets[index] || activeDataSets?.length === 0
                          ? 1
                          : 0.5,
                      cursor:
                        activeDataSets?.length < (isWeb ? 2 : 3) ? 'default' : 'pointer',
                    }}
                  >
                    {formatButtonValue(
                      props?.db_fields?.[index],
                      t(button),
                      chartData?.[activeIndex as any]?.[
                        props.db_fields?.[index]
                      ]?.toLocaleString(getNumberFormatLocale()) || null
                    )}
                    <div
                      className={styles.CombinedBatteryChart__Button__Underline}
                      style={{
                        backgroundColor:
                          activeDataSets[index] || activeDataSets?.length === 0
                            ? `${ThemeStore?.colors[props.colors[index].color]}${props.colors[index].colorShade}`
                            : getGreyScaledColor(`${ThemeStore?.colors[props.colors[index].color]}${props.colors[index].colorShade}`),
                      }}
                    ></div>
                  </div>
                );
              })
          }
        </div>
        {isNullStateActive
          ? <BatterySkeleton isNull={isNullStateActive && LOADING_STATE !== 'PENDING'} />
          : (
            <div className={styles.CombinedBatteryChart__Chart__Wrapper}
              {...(!isWeb ? {
                onTouchMove: handleTouchMove,
                onTouchEnd: handleTouchEnd,
                onTouchEndCapture: handleTouchEnd,
                ref: chartRef
              } : {})}
            >
              <div
                className={isWeb
                  ? `${styles.CombinedBatteryChart__Chart__Unit} ${styles.CombinedBatteryChart__Chart__Unit__Left} ${styles.CombinedBatteryChart__Chart__Unit__Left__Web}`
                  : `${styles.CombinedBatteryChart__Chart__Unit} ${styles.CombinedBatteryChart__Chart__Unit__Left}`}
              >
                {unit}
              </div>
              <ResponsiveContainer
                width="100%"
                height={isWeb ? 305 : 250}
                {...(!isWeb ? { ref: chartRef } : {})}
                style={isWeb ? { marginTop: 26 } : undefined}
              >
                <ComposedChart
                  data={chartData}
                  margin={isWeb
                    ? { top: 25, right: (innerTimeSet === 'day' ? -15 : 10), left: -25, bottom: 0 }
                    : { top: 20, right: (innerTimeSet === 'day' ? -30 : 20), left: -25, bottom: 0 }}
                  onMouseLeave={isWeb ? handleMouseLeave : undefined}
                  onMouseMove={isWeb ? (e => handleTouchMove(e)) : undefined}
                  stackOffset={!isWeb ? 'sign' : undefined}
                  width={!isWeb ? 300 : undefined}
                  id={!isWeb ? 'combinedBatteryChart' : undefined}
                >
                  <XAxis
                    dataKey="timestamp"
                    tick={{
                      fontSize: 12,
                      fontFamily: 'Flexo',
                      fill: ThemeStore?.colors?.greyscale,
                      opacity: 1,
                      fontWeight: 300
                    }}
                    ticks={chartData.map((_: any, index: number) =>
                      formatDate(rawData?.[index]?.timestamp, innerTimeSet)
                    )}
                    tickFormatter={(tick: any) => tick}
                    interval={getInterval()}
                    axisLine={false}
                    tickLine={false}
                    tickMargin={12}
                    dy={-6}
                    {...(!isWeb ? {
                      onTouchEnd: handleTouchEnd,
                      onTouchEndCapture: handleTouchEnd,
                      ref: xRef
                    } : {})}
                  />
                  <CartesianGrid
                    strokeDasharray="0"
                    vertical={false}
                    horizontalValues={[0]}
                    stroke={ThemeStore?.colors?.gridColor}
                    strokeWidth={0.5}
                    {...(!isWeb ? {
                      onTouchEnd: handleTouchEnd,
                      onTouchEndCapture: handleTouchEnd
                    } : {})}
                  />
                  <YAxis
                    yAxisId="left"
                    orientation="left"
                    tick={{
                      fontSize: 12,
                      fontFamily: 'Flexo',
                      fill: ThemeStore?.colors?.greyscale,
                      opacity: 1,
                      fontWeight: 300,
                      dy: -4
                    }}
                    axisLine={true}
                    tickLine={false}
                    ticks={getSymmetricTicks(chartData)}
                    tickFormatter={(tick: any) => (tick).toLocaleString(getNumberFormatLocale(), { maximumFractionDigits: 1 })}
                    tickMargin={8}
                    dx={0}
                    strokeWidth={isWeb ? 1 : 0.1}
                    stroke={ThemeStore?.colors?.gridColor}
                    opacity={0.2}
                    {...(!isWeb ? {
                      onTouchEnd: handleTouchEnd,
                      onTouchEndCapture: handleTouchEnd
                    } : {})}
                  />
                  {innerTimeSet === 'day' && props?.context === 'site' && (
                    <YAxis
                      yAxisId="right"
                      orientation="right"
                      tick={{
                        fontSize: 12,
                        fontFamily: 'Flexo',
                        fill: ThemeStore?.colors?.greyscale,
                        opacity: 1,
                        fontWeight: 300,
                        dy: -4
                      }}
                      domain={[0, 100]}
                      axisLine={true}
                      tickLine={false}
                      tickMargin={8}
                      dx={0}
                      strokeWidth={isWeb ? 1 : 0.1}
                      stroke={ThemeStore?.colors?.gridColor}
                      opacity={0.2}
                      {...(!isWeb ? {
                        onTouchEnd: handleTouchEnd,
                        onTouchEndCapture: handleTouchEnd
                      } : {})}
                    />
                  )}
                  {/* Bar/Line rendering differentiated */}
                  {isWeb ? (
                    <Bar
                      yAxisId="left"
                      dataKey={props.db_fields[0]}
                      hide={
                        activeDataSets.length === 0
                          ? false
                          : !activeDataSets?.[0] || false
                      }
                      onClick={(_, i) => handleBarClick(i)}
                      onMouseMove={(_, i) => handleMouseMove(i)}
                      radius={[4, 4, 4, 4]}
                    >
                      {chartData.map((entry: any, index: number) => (
                        <Cell
                          key={`cell-${entry.timestamp}}`}
                          fill={getFillColor(index, 0)}
                        />
                      ))}
                    </Bar>
                  ) : (
                    <>
                      <Bar
                        stackId={'battery'}
                        yAxisId="left"
                        dataKey={props.db_fields[0]}
                        hide={
                          activeDataSets.length === 0
                            ? false
                            : !activeDataSets?.[0] || false
                        }
                        radius={[4, 4, 4, 4]}
                        strokeWidth={3}
                      >
                        {chartData.map((entry: any, index: number) => (
                          <Cell
                            key={`cell-${entry.timestamp}`}
                            fill={getFillColor(index, 0)}
                          />
                        ))}
                      </Bar>
                      <Bar
                        stackId={'battery'}
                        yAxisId="left"
                        dataKey={props.db_fields[1]}
                        hide={
                          activeDataSets.length === 0
                            ? false
                            : !activeDataSets?.[1] || false
                        }
                        radius={[4, 4, 4, 4]}
                        strokeWidth={3}
                      >
                        {chartData.map((entry: any, index: number) => (
                          <Cell
                            key={`cell-${entry.timestamp}-negative`}
                            fill={getFillColor(index, 1)}
                          />
                        ))}
                      </Bar>
                    </>
                  )}
                  {innerTimeSet === 'day' && props?.context === 'site' && (
                    <Line
                      hide={!activeDataSets[isWeb ? 1 : 2]}
                      yAxisId="right"
                      type="monotone"
                      dataKey={props.db_fields[isWeb ? 1 : 2]}
                      stroke={`${ThemeStore?.colors[props.colors[isWeb ? 1 : 2].color]}${props.colors[isWeb ? 1 : 2].colorShade}`}
                      strokeWidth={3}
                      dot={CustomizedDot}
                      strokeLinecap='round'
                      {...(!isWeb ? {
                        onTouchEnd: handleTouchEnd,
                        onTouchEndCapture: handleTouchEnd
                      } : {})}
                    />
                  )}
                </ComposedChart>
              </ResponsiveContainer>
              <div
                className={isWeb
                  ? `${styles.CombinedBatteryChart__Chart__Unit} ${styles.CombinedBatteryChart__Chart__Unit__Right} ${styles.CombinedBatteryChart__Chart__Unit__Right__Web}`
                  : `${styles.CombinedBatteryChart__Chart__Unit} ${styles.CombinedBatteryChart__Chart__Unit__Right}`}
              >
                {innerTimeSet === 'day' && props?.context === 'site' ? '%' : ''}
              </div>
            </div>
          )}
      </>
    </div>
  );
}

export default BatteryChart; 
